import reactUpdate from 'immutability-helper';

import {
  ChemistryReactionRole,
  CHEMISTRY_REACTION_ROLE_DISPLAY_NAME_TO_ENUM,
} from 'notebook/integrations/chemistry/ChemistryStructuredTableConstants';
import {
  extractLotProperties,
  implementsMoleculeLotInterface,
} from 'notebook/integrations/chemistry/custom-evals/LotPropertiesCustomEval';
import {
  StoichiometryStructuredTable,
  StoichiometryTableRow,
} from 'notebook/integrations/chemistry/StoichiometryStructuredTableAdapter';
import {
  // eslint-disable-next-line camelcase
  GetLotPropertiesQuery_bioentities_MoleculeNode as MoleculeIdentityNode,
  // eslint-disable-next-line camelcase
  GetLotPropertiesQuery_bioentities_7fiWt0WJ4GzD91Z2J0SVRbzSlLO68dPgO2ZwCE4fEA as LotBioentityNode,
} from 'notebook/structured-tables/molecules/__generated/GetLotPropertiesQuery';
import {StoichiometryLotRowUpdater} from 'notebook/structured-tables/row-updaters/StoichiometryLotRowUpdater';
import {
  PrimarySampleLookupType,
  StructuredTableColumnGroups,
  StructuredTableColumn,
} from 'notebook/structured-tables/StructuredTableConstants';
import {Cell} from 'notebook/tables/constants/CellConstants';
import {EvaluatedCell} from 'notebook/tables/excess/EvaluationConstants';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';

export function getMoleculeIdFromEvaluatedCell(cell: EvaluatedCell): string | null {
  if (cell.value?.type === BaseTypedValueType.BIOENTITY && cell.value.bioentitySummary?.id) {
    return cell.value.bioentitySummary.id;
  }
  return null;
}

export function getRoleFromEvaluatedCell(cell: EvaluatedCell): ChemistryReactionRole | null {
  if (cell.value?.type === BaseTypedValueType.STRING && cell.value.rawValue) {
    if (CHEMISTRY_REACTION_ROLE_DISPLAY_NAME_TO_ENUM[cell.value.rawValue]) {
      return CHEMISTRY_REACTION_ROLE_DISPLAY_NAME_TO_ENUM[cell.value.rawValue]!;
    }
  }
  return null;
}

export function getColumnIndices(columns: Array<StructuredTableColumn>): {
  compoundColumnIndex: number;
  roleColumnIndex: number;
  /** May be -1: not every Stoichiometry table variant has a Lot column. */
  lotColumnIndex: number;
} {
  const compoundColumnIndex = columns.findIndex(
    (col) => col.columnGroup === StructuredTableColumnGroups.CHEMISTRY_MOLECULE
  );
  const roleColumnIndex = columns.findIndex(
    (col) => col.columnGroup === StructuredTableColumnGroups.CHEMISTRY_REACTION_ROLE
  );
  const lotColumnIndex = columns.findIndex(
    (col) => col.columnGroup === StructuredTableColumnGroups.CHEMISTRY_LOT
  );

  if (compoundColumnIndex === -1 || roleColumnIndex === -1) {
    throw new Error('Could not find required columns');
  }
  return {compoundColumnIndex, roleColumnIndex, lotColumnIndex};
}

function getCompoundCellTypedValue(cell: Cell): TypedValue | null {
  return cell.typedValue ?? null;
}

function getCachedCurrentVersionId(node: LotBioentityNode | MoleculeIdentityNode | null): string | undefined {
  return node?.__typename === 'MoleculeNode' ? node.currentVersion.id : undefined;
}

/** The row's Compound cell identity as resolved synchronously from raw (pre-evaluation) cell data. */
type ResolvedMoleculeIdentity =
  | {status: 'resolved'; moleculeId: string}
  | {status: 'empty'}
  // 'unknown': the Lot cell's parent molecule isn't in the Apollo cache yet -- treat as "no information".
  | {status: 'unknown'};

/** Resolves via the Compound cell's raw typedValue (direct selection), falling back to the Lot cell's raw typedValue + its cached parent molecule (Lot-derived selection). */
function getResolvedMoleculeIdentityForRow(
  row: StoichiometryTableRow,
  compoundColumnIndex: number,
  lotColumnIndex: number
): ResolvedMoleculeIdentity {
  const compoundCellTypedValue = getCompoundCellTypedValue(row.cells[compoundColumnIndex]);
  if (compoundCellTypedValue?.type === BaseTypedValueType.BIOENTITY) {
    const moleculeId = compoundCellTypedValue.bioentitySummary?.id;
    return moleculeId != null ? {status: 'resolved', moleculeId} : {status: 'empty'};
  }
  if (compoundCellTypedValue?.type === BaseTypedValueType.STRING && compoundCellTypedValue.rawValue !== '') {
    return {status: 'unknown'};
  }

  const lotCellTypedValue =
    lotColumnIndex === -1 ? null : getCompoundCellTypedValue(row.cells[lotColumnIndex]);
  if (lotCellTypedValue?.type !== BaseTypedValueType.BIOENTITY) {
    // Only a positive signal (explicit clear, raw typedValue === null) counts as "empty" -- undefined
    // (untouched) is inconclusive, not a clear.
    // eslint-disable-next-line benchlint/no-eqeqeq-null
    return row.cells[compoundColumnIndex].typedValue === null ? {status: 'empty'} : {status: 'unknown'};
  }

  const lotId = lotCellTypedValue.bioentitySummary?.id;
  if (lotId == null) {
    return {status: 'unknown'};
  }
  const cachedLot = StoichiometryLotRowUpdater.readFromCache({
    idForLookup: lotId,
    sampleType: PrimarySampleLookupType.DIRECT,
  });
  if (cachedLot == null || !implementsMoleculeLotInterface(cachedLot)) {
    return {status: 'unknown'};
  }
  const parentMoleculeId = extractLotProperties(cachedLot.tagsJson).parentMoleculeId;
  return parentMoleculeId != null ? {status: 'resolved', moleculeId: parentMoleculeId} : {status: 'empty'};
}

function shouldClearDrawnMolfile(row: StoichiometryTableRow, compoundColumnIndex: number): boolean {
  const metadata = row.metadata;
  if (metadata?.drawnMolfile == null) {
    return false;
  }

  const cellTypedValue = getCompoundCellTypedValue(row.cells[compoundColumnIndex]);
  const moleculeId =
    cellTypedValue?.type === BaseTypedValueType.BIOENTITY
      ? (cellTypedValue.bioentitySummary?.id ?? null)
      : null;

  if (metadata.drawnMolfileMoleculeId != null) {
    return moleculeId !== metadata.drawnMolfileMoleculeId;
  }

  const canonicalSmiles = metadata.canonicalSmiles;
  if (canonicalSmiles != null) {
    if (cellTypedValue?.type === BaseTypedValueType.STRING) {
      return cellTypedValue.rawValue !== canonicalSmiles;
    }
    return true;
  }

  return false;
}

type EditorMetadataField =
  | 'drawnMolfile'
  | 'drawnMolfileMoleculeId'
  | 'canonicalSmiles'
  | 'moleculeId'
  | 'moleculeVersionId';

function getEditorMetadataFieldsToClear(
  row: StoichiometryTableRow,
  compoundColumnIndex: number,
  resolvedIdentity: ResolvedMoleculeIdentity
): Array<EditorMetadataField> {
  const metadata = row.metadata;
  if (metadata == null) {
    return [];
  }
  const hasDrawnMolfile = metadata.drawnMolfile != null;
  const hasCanonicalSmiles = metadata.canonicalSmiles != null;
  const hasMoleculeId = metadata.moleculeId != null;
  if (!hasDrawnMolfile && !hasCanonicalSmiles && !hasMoleculeId) {
    return [];
  }

  const cellTypedValue = getCompoundCellTypedValue(row.cells[compoundColumnIndex]);
  const moleculeId =
    cellTypedValue?.type === BaseTypedValueType.BIOENTITY
      ? (cellTypedValue.bioentitySummary?.id ?? null)
      : null;
  const cellSmiles = cellTypedValue?.type === BaseTypedValueType.STRING ? cellTypedValue.rawValue : null;
  const isEmpty =
    cellTypedValue == null || (cellTypedValue.type === BaseTypedValueType.STRING && cellSmiles === '');

  const fieldsToClear: Array<EditorMetadataField> = [];

  if (hasCanonicalSmiles && moleculeId == null) {
    if (isEmpty || cellSmiles !== metadata.canonicalSmiles) {
      fieldsToClear.push('canonicalSmiles');
    }
  }

  if (hasDrawnMolfile && shouldClearDrawnMolfile(row, compoundColumnIndex)) {
    fieldsToClear.push('drawnMolfile');
    if (metadata.drawnMolfileMoleculeId != null) {
      fieldsToClear.push('drawnMolfileMoleculeId');
    }
  }

  if (hasMoleculeId) {
    const resolvedMoleculeId = resolvedIdentity.status === 'resolved' ? resolvedIdentity.moleculeId : null;
    if (resolvedIdentity.status !== 'unknown' && resolvedMoleculeId !== metadata.moleculeId) {
      fieldsToClear.push('moleculeId', 'moleculeVersionId');
    }
  }

  return fieldsToClear;
}

function getMoleculeIdentityBaselineToEstablish(
  row: StoichiometryTableRow,
  resolvedIdentity: ResolvedMoleculeIdentity
): {moleculeId: string; moleculeVersionId?: string} | null {
  if (resolvedIdentity.status !== 'resolved') {
    return null;
  }
  const {moleculeId} = resolvedIdentity;
  if (moleculeId === row.metadata?.moleculeId) {
    return null;
  }

  const cached = StoichiometryLotRowUpdater.readFromCache({
    idForLookup: moleculeId,
    sampleType: PrimarySampleLookupType.DIRECT,
  });
  const moleculeVersionId = getCachedCurrentVersionId(cached);
  if (moleculeVersionId != null) {
    return {moleculeId, moleculeVersionId};
  }
  // Not yet in cache; the passive live-data fetch or the next resync will backfill moleculeVersionId.
  return {moleculeId};
}

export function reconcileCompoundRowMetadata(
  structuredTable: StoichiometryStructuredTable
): StoichiometryStructuredTable {
  const {compoundColumnIndex, lotColumnIndex} = getColumnIndices(structuredTable.columns);
  let rowsUpdate: Record<number, object> | null = null;

  structuredTable.rows.forEach((row, rowIndex) => {
    // Resolved once per row and shared below to avoid duplicate Apollo reads.
    const resolvedIdentity = getResolvedMoleculeIdentityForRow(row, compoundColumnIndex, lotColumnIndex);
    const fieldsToClear = getEditorMetadataFieldsToClear(row, compoundColumnIndex, resolvedIdentity);
    const baselineToEstablish = getMoleculeIdentityBaselineToEstablish(row, resolvedIdentity);

    if (fieldsToClear.length === 0 && baselineToEstablish == null) {
      return;
    }
    if (rowsUpdate == null) {
      rowsUpdate = {};
    }
    const metadataUpdate: {$unset?: Array<EditorMetadataField>; $merge?: object} = {};
    if (fieldsToClear.length > 0) {
      metadataUpdate.$unset = fieldsToClear;
    }
    if (baselineToEstablish != null) {
      metadataUpdate.$merge = baselineToEstablish;
    }
    rowsUpdate[rowIndex] = {metadata: metadataUpdate};
  });

  if (rowsUpdate == null) {
    return structuredTable;
  }
  return reactUpdate(structuredTable, {rows: rowsUpdate});
}
